import { Box, Button, Flex, Image, Radio, RadioGroup, Text, VStack } from "@chakra-ui/react";
import React, { useState } from "react";
import { Icons } from "../../Assets/icons";
import { RelevanceFieldsNewArr } from "../../Constants/data";
import { useToastWrapper } from "../../Wrapper/toastWrapper";
import { errorHandler } from "../../Utils/handler";
import { BuyerAPI } from "../../Apis/buyerAPI";
import { CommonAPI } from "../../Apis/commonApi";
import moment from "moment";

interface RelevanceData {
  prequalified_company_name: string;
  prequalified_user_id: string;
  event_id: string;
  status: string;
}
type Props = {
  handleCloseModal: () => void;
  relaodData: (newStatus?: string) => void; // Claude: optional newStatus — callers that do full reload can ignore it 10-03-2026
  data: RelevanceData;
};

// Claude: Ignore reason options for notes — mirrors RelevanceModal.tsx 20-02-2026
const IGNORE_REASONS = [
  "Bad experience in the past",
  "Not verified / Not credible",
  "Company profile is not relevant",
];

const RelevanceModalBuyer = (props: Props) => {
  const { error } = useToastWrapper();
  // Claude: Ignore reason prompt state 20-02-2026
  const [showIgnorePrompt, setShowIgnorePrompt] = useState(false);
  const [selectedReason, setSelectedReason] = useState<string>("");
  const [saving, setSaving] = useState(false);

  const HandleSubmit = async (status: String) => {
    // Claude: intercept Ignore — show reason prompt before saving 20-02-2026
    if (status === "Not_Relevant") {
      setShowIgnorePrompt(true);
      return;
    }
    await saveRelevance(status);
  };

  const saveRelevance = async (status: String) => {
    try {
      // Claude: omit event_id if empty — @IsMongoId() rejects "" but field is @IsOptional() 10-03-2026
      const payload: any = {
        prequalified_company_name: props.data.prequalified_company_name,
        prequalified_user_id: props.data.prequalified_user_id,
        status,
      };
      if (props.data.event_id) payload.event_id = props.data.event_id;
      const res = await BuyerAPI.PostRelevanceSeller(payload);
      if (res) {
        props.handleCloseModal();
        props.relaodData(status as string); // Claude: pass new status so caller can update local state 10-03-2026
      }
    } catch (err) {
      console.log(err);
      error(errorHandler(err), "");
    }
  };

  // Claude: prepend timestamped ignore reason to seller's notes, then save relevance 20-02-2026
  const saveIgnoreWithNotes = async () => {
    setSaving(true);
    try {
      const timestamp = moment().format("DD-MMM-YYYY hh:mm A");
      const notePrefix = `${timestamp}: ${selectedReason}`;

      let existingNotes = "";
      try {
        const res = await CommonAPI.getNotes({
          eventId: props.data.event_id,
          forUser: props.data.prequalified_user_id,
        });
        existingNotes = res?.data?.data?.body || "";
      } catch (_) {}

      const updatedNotes = existingNotes
        ? `${notePrefix}\n${existingNotes}`
        : notePrefix;

      await CommonAPI.postNotes({
        eventId: props.data.event_id,
        forUser: props.data.prequalified_user_id,
        body: updatedNotes,
      });

      await saveRelevance("Not_Relevant");
    } catch (err) {
      console.log(err);
      error(errorHandler(err), "");
    } finally {
      setSaving(false);
    }
  };

  // Claude: Ignore reason prompt sub-view 20-02-2026
  if (showIgnorePrompt) {
    return (
      <Box w={"100%"} h={"100%"}>
        {/* Claude: Flex header — title centered, X on right 23-02-2026 */}
        <Flex alignItems={"center"} mb={2}>
          <Box flex={1} />
          <Text fontSize={"15px"} fontWeight={700} color={"black"}>Relevance</Text>
          <Flex flex={1} justifyContent={"flex-end"}>
            <Image onClick={() => props.handleCloseModal()} src={Icons.cross} cursor={"pointer"} />
          </Flex>
        </Flex>

        <Box px={3} pb={2} pt={0}>
          <Text fontSize={"13px"} fontWeight={"bold"} mb={2}>
            Add ignore reason to notes:
          </Text>
          <RadioGroup value={selectedReason} onChange={setSelectedReason}>
            <VStack align={"start"} spacing={2}>
              {IGNORE_REASONS.map((reason, i) => (
                <Radio key={i} value={reason} colorScheme="gray" size="sm">
                  <Text fontSize={"13px"}>{reason}</Text>
                </Radio>
              ))}
            </VStack>
          </RadioGroup>
        </Box>

        <Flex w={"100%"} justifyContent={"space-between"} px={3} pb={3} gap={2}>
          <Button
            onClick={async () => {
              setSaving(true);
              await saveRelevance("Not_Relevant");
              setSaving(false);
            }}
            flex={1}
            size={"md"}
            variant={"outline"}
            borderColor={"card.borderLight"}
            _hover={{ background: "secondary.light" }}
            borderRadius={"2px"}
            isLoading={saving}
          >
            <Text fontSize={"sm"} fontWeight={"regular"}>Skip notes & save</Text>
          </Button>
          <Button
            onClick={saveIgnoreWithNotes}
            flex={1}
            size={"md"}
            bg={"#D4EDDA"}
            color={"#155724"}
            _hover={{ bg: "#C3E6CB" }}
            borderRadius={"2px"}
            isDisabled={!selectedReason}
            isLoading={saving}
          >
            <Text fontSize={"sm"} fontWeight={"regular"}>Add notes & save</Text>
          </Button>
        </Flex>
      </Box>
    );
  }

  // Claude: Tighter layout — reduced font, padding, gaps 23-02-2026
  return (
    <Box w={"100%"} h={"100%"}>
      {/* Claude: Flex header — title centered, X on right 23-02-2026 */}
      <Flex alignItems={"center"} mb={2}>
        <Box flex={1} />
        <Text fontSize={"15px"} fontWeight={700} color={"black"}>Relevance</Text>
        <Flex flex={1} justifyContent={"flex-end"}>
          <Image onClick={() => props.handleCloseModal()} src={Icons.cross} cursor={"pointer"} />
        </Flex>
      </Flex>

      <Box px={3} pb={2} pt={0}>
        <Text fontSize={"13px"} fontWeight={"bold"}>
          Is {props.data.prequalified_company_name} relevant to you?
        </Text>
      </Box>

      {/* Claude: scrollable relevance options 25-03-2026 */}
      <Box flexGrow={1} px={3} pb={3} maxH="50vh" overflowY="auto">
        <Flex direction={"column"} gap={"10px"}>
          {RelevanceFieldsNewArr.map((field, index) => (
            <Flex
              key={index}
              p={2}
              background={props?.data?.status == field.status ? "#E0E0E0" : "#FAFAFA"}
              borderRadius={"10px"}
              fontSize={"13px"}
              fontWeight={"600"}
              justifyContent={"space-between"}
              alignItems={"center"}
              cursor={"pointer"}
              onClick={() => HandleSubmit(field.status)}
            >
              <Flex alignItems={"center"} gap={"8px"}>
                <Box
                  h={"14px"}
                  w={"14px"}
                  borderRadius={"2px"}
                  background={field.color}
                  flexShrink={0}
                />
                {field.title}
              </Flex>
            </Flex>
          ))}
        </Flex>
      </Box>
    </Box>
  );
};

export default RelevanceModalBuyer;
